import './polyfill';
import Vue from 'vue';
import { get } from 'lodash';
import { getI18n } from './locales';
import App from './App';
import router from './router';
import psHttp, { generateExceptionRedirector, psAxios } from '@patsnap/common-ps-http';
import setWebsitFont from 'library-@/utils/configure-website-font';
import { prepareTokenBase, updateTokenBaseRequestHeaders } from 'library-@/token-base/for-new-projects';
import { PRESETS_TOKEN_BASE_HOMEPAGE } from 'library-@/token-base/presets';
import defer from 'library-@/startup/defer';
import { initTransformCodeInterceptor } from 'library-@/apis/interceptors/transform_code.interceptor';
import { RavenInsertMeta } from 'library-@/services/sentry';
import hackPopupManager from 'library-@/utils/hackPopupManager';
import store from './store';
import setTitle from './utils/setTitle';
import { createUniformSetting } from '@patsnap/uniform-setting';
import { HeaderService } from '@patsnap-utils/biz';
import { accountApiUrlPrefix } from 'library-@/config/constants';
import { FE_ACCOUNT_URI } from './constants';
import { getInit, getBasic } from 'library-@/apis/home_api/homepage-api';
import { getSuccessPageApi } from 'library-@/apis/account_api/account';
import { initLegalStatus } from 'library-@/components/legal-tag/legal-config';
import { loadAilArms } from '@home/shared/utils/ail-arms';
import { SpaMode } from '@home/shared/types/arms';
// 引入初始化样式
import 'library-@/assets/styles/reset.scss';
import './assets/styles/el-overwrites/index.scss';
import './assets/styles/app.scss';
import '@patsnap-biz/design-tokens/scss/_variables-IP.css';

Vue.config.productionTip = false;

// 同步 element 和 patsnap 的 z-index
hackPopupManager();

// 初始化埋点
window.rvTrack = (action, attribute, moduleName = 'Homepage') => RavenInsertMeta(moduleName, action, attribute);

(async () => {
    // TokenBase
    await prepareTokenBase({
        ...PRESETS_TOKEN_BASE_HOMEPAGE
    });

    // 准备获取语言配置
    const headerService = new HeaderService();
    const setting = createUniformSetting({
        verbose: true,
        axiosInstance: psAxios,
        loggedIn: true,
        baseAccountUrl: accountApiUrlPrefix,
        locale: {
            supports: ['cn', 'en', 'tw', 'jp'],
        }
    });

    // 获取初始化数据
    const [[accountInfo, selfTrailUserUrl], i18n, initData, , legalConfig] = await Promise.all([
        /* 1, account info */
        headerService.sync({ http: psHttp, uri: accountApiUrlPrefix }).then(async (accountInfo) => {
            /* 自注册用户获取初始化跳转链接 */
            const isSelfTrail = get(accountInfo, 'self_trail', false);
            let selfTrailUserUrl;
            if (isSelfTrail) {
                const successPage = await getSuccessPageApi({
                    request_from: 'w-analytics-homepage'
                });
                selfTrailUserUrl = get(successPage, 'page', '');
            }
            return [get(headerService, 'account._info', {}), selfTrailUserUrl];
        }),
        /* 2, language */
        setting.locale.getLocale().then(async ({ user: siteLang, default: defaultSiteLang }) => {
            const lang = siteLang || defaultSiteLang;
            setWebsitFont(lang);
            updateTokenBaseRequestHeaders({ 'X-Site-Lang': lang.toUpperCase() });
            const i18n = await getI18n(lang);
            initTransformCodeInterceptor(i18n);
            return i18n;
        }),
        /* 3, module init */
        getInit().catch(generateExceptionRedirector(403, `${FE_ACCOUNT_URI}/user-settings/#/no-permission?from_site=home`)),
        /* 3, basic前置 防止页面抖动 */
        getBasic(),
        initLegalStatus()
    ]);

    setTitle({ locale: i18n.locale, isWhiteLabel: headerService.whiteLabel });
    
    // 加载ail-arms
    loadAilArms({ account_id: accountInfo.account_id, spaMode: SpaMode.Hash });

    // 初始化 zPREFACE
    const zPREFACE = {
        NAME: 'Homepage',
        accountInfo,
        globalConfig: {
            ...get(initData, 'globalConfig', {}),
            // 原有公共组件需要用到这些变量
            currentLanguage: i18n.locale,
            userInfo: {
                isSpecialIp: get(accountInfo, 'special_ip'),
                encryptId: get(accountInfo, 'encrypted_account_id'),
                email: get(accountInfo, 'email')
            },
            companyInfo: {
                name: get(accountInfo, 'company_name')
            },
            featureConfigs: {
                smartSearch: get(initData, 'permission.smart_search', false),
                previewNumber: 1500,
                maxQueryLength: 20000
            }
        },
        pageConfig: {
            hasTopic: get(initData, 'has_topic', false)
        },
        permissions: get(initData, 'permission', {}),
        legalConfig: get(legalConfig, 'legalConfig', [])
    };
    window.zPREFACE = zPREFACE;

    // 自注册用户跳转逻辑
    // 如果url中包含onboarding，则跳转，避免重新进入Home首页
    if (selfTrailUserUrl && selfTrailUserUrl.indexOf('onboarding') > -1) window.location.href = selfTrailUserUrl;

    Vue.mixin({
        data() {
            return {
                headerService,
            };
        },
        created() {
            this.$setting = setting;
        }
    });

    const vm = new Vue({
        el: '#app',
        i18n,
        router,
        store,
        mounted() {
            // 延迟加载不需要立即加载的组件
            defer(Vue, 'Homepage', headerService.account, { sentry: true }, i18n.locale);
        },
        render: h => h(App)
    });
    window.hidePageLoading && window.hidePageLoading();
    // 更新vuex
    vm.$store.dispatch('root/setPermissions', zPREFACE.permissions);
    vm.$store.dispatch('root/setCurrentLang', i18n.locale);
})();
